'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { defaultSchema } from 'hast-util-sanitize'
import {
  Activity,
  Building2,
  Calendar,
  Clock,
  ExternalLink,
  FileText,
  Files,
  Layers,
  ListFilter,
  Sparkles,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import { twMerge } from 'tailwind-merge'

import { ToggleBookmarkIconButton } from '@/components/bookmarks/ToggleBookmarkIconButton'
import { useCreateOpportunityChatSession } from '@/components/chat-v2/hooks/useChatSession'
import Button from '@/components/common/Button'
import { ResourcePermissionGate } from '@/components/common/PermissionGate'
import {
  PursuitActions,
  buildSbirInput,
} from '@/components/opportunity/common/PursuitActionsV2'
import { markdownComponents } from '@/components/pursuits/wiki/ui/markdown'

import { cleanFileName } from '@/helpers/file'

import { useQuerySearchId } from '@/hooks/common/useQuerySearchId'
import { useRenderNowMs } from '@/hooks/common/useRenderNowMs'
import { useSafePush } from '@/hooks/navigation/useSafePush'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import { isUUID } from '@/types/Id'
import type { Sbir } from '@/types/sbir/Sbir'
import type { SearchOut } from '@/types/Search'

import {
  OpportunityDocumentsSection,
  OpportunityDocumentsTab,
  PageTitleV2,
  ShareIcon,
  SimilarOpportunitiesV2,
  ViewDocumentsButton,
} from '../../common'
import { BadgeV2 } from '../../common/BadgeV2'
import { CardV2 } from '../../common/CardV2'
import { ContentPanelV2 } from '../../common/ContentPanelV2'
import { DataBlockV2 } from '../../common/DataBlockV2'
import { useDaysRemaining } from '../../common/hooks'
import { SidebarWidgetV2 } from '../../common/SidebarWidgetV2'
import { TabPanelV2 } from '../../common/TabPanelV2'
import { TimelineV2 } from '../../common/TimelineV2'
import { normalizeSbirData } from './transforms'

// Safe HTML schema for description content
const safeHtmlSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    'mark',
    'u',
    'sub',
    'sup',
    'del',
    'ins',
  ],
}

// ============================================================================
// Types
// ============================================================================

interface SbirDisplayV2Props {
  sbir: Sbir
  similarOpportunities?: SearchOut[]
  isSimilarLoading?: boolean
  onShare?: () => void
  /** Custom title for the timeline widget (default: "Key Dates") */
  timelineTitle?: string
}

type DocumentsTabEntrySource = 'default' | 'compact-file-click'

// ============================================================================
// Main Component
// ============================================================================

const SbirDisplayV2: React.FC<SbirDisplayV2Props> = ({
  sbir,
  similarOpportunities = [],
  isSimilarLoading = false,
  onShare,
  timelineTitle = 'Key Dates',
}) => {
  const referenceNowMs = useRenderNowMs({ refreshMs: 60_000 })
  const data = useMemo(
    () => normalizeSbirData(sbir, referenceNowMs ?? Date.now()),
    [referenceNowMs, sbir],
  )
  const { searchId } = useQuerySearchId()
  const { activeOrgId } = useActiveOrgId()
  const { safePush } = useSafePush()
  const {
    mutateAsync: createOpportunityChatSession,
    isPending: isCreatingOpportunityChatSession,
  } = useCreateOpportunityChatSession()

  const [activeTab, setActiveTab] = useState('analysis')
  const [documentsTabEntrySource, setDocumentsTabEntrySource] =
    useState<DocumentsTabEntrySource>('default')
  const [selectedDocumentFileId, setSelectedDocumentFileId] = useState<
    string | null
  >(null)
  const [documentFileScrollRequestKey, setDocumentFileScrollRequestKey] =
    useState<number | null>(null)
  const isDocumentsTab = activeTab === 'documents'

  const goToDocumentsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  useEffect(() => {
    return () => {
      if (goToDocumentsTimeoutRef.current) {
        clearTimeout(goToDocumentsTimeoutRef.current)
      }
    }
  }, [])

  const handleActiveTabChange = useCallback((nextTab: string) => {
    if (nextTab === 'documents') {
      setDocumentsTabEntrySource('default')
    }
    setActiveTab(nextTab)
  }, [])

  const goToDocumentsTab = useCallback(
    (fileId?: string | null, source: DocumentsTabEntrySource = 'default') => {
      setDocumentsTabEntrySource(source)
      setActiveTab('documents')
      if (fileId) {
        setSelectedDocumentFileId(fileId)
        setDocumentFileScrollRequestKey(Date.now())
      }

      if (goToDocumentsTimeoutRef.current) {
        clearTimeout(goToDocumentsTimeoutRef.current)
      }

      goToDocumentsTimeoutRef.current = setTimeout(() => {
        document
          .getElementById('solicitation-documents-tab-panel')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    },
    [],
  )

  const analyzeDocumentFile = useCallback(
    async (file: { name: string }) => {
      if (!isUUID(activeOrgId)) return

      const session = await createOpportunityChatSession({
        opportunity_id: sbir.id,
        opportunity_type: 'sbir',
        organization_id: activeOrgId,
        title: `${sbir.title}: ${cleanFileName(file.name)}`,
      })

      if (session) {
        safePush(`/orgs/${activeOrgId}/c/opp/${session.id}`)
      }
    },
    [activeOrgId, createOpportunityChatSession, safePush, sbir.id, sbir.title],
  )

  // Build opportunity input for pursuit actions
  const opportunityInput = useMemo(
    () =>
      buildSbirInput({
        id: sbir.id,
        title: sbir.title,
        application_due_date: sbir.application_due_date,
        ai_summary: sbir.ai_summary,
      }),
    [sbir.id, sbir.title, sbir.application_due_date, sbir.ai_summary],
  )

  // Shared hooks
  const { daysRemaining, isPast: isClosed } = useDaysRemaining(sbir.close_date)

  // Determine status for badge
  const getStatusVariant = (
    status: string,
  ): 'success' | 'warning' | 'neutral' => {
    const s = status.toLowerCase()
    if (s.includes('closed')) return 'neutral'
    if (s.includes('open') || s.includes('active')) return 'success'
    if (s.includes('pre') || s.includes('future')) return 'warning'
    return 'neutral'
  }

  return (
    <>
      {/* Header Block */}
      <PageTitleV2>
        <PageTitleV2.Badges>
          <BadgeV2 variant="blue">{sbir.program}</BadgeV2>
          <BadgeV2 variant={getStatusVariant(sbir.status)} icon={Activity}>
            {sbir.status}
          </BadgeV2>
        </PageTitleV2.Badges>
        <PageTitleV2.Content className="lg:max-w-full">
          <PageTitleV2.Title>{sbir.title}</PageTitleV2.Title>
        </PageTitleV2.Content>
        <PageTitleV2.Row className="lg:flex-row lg:items-center lg:justify-between lg:gap-4">
          <PageTitleV2.Content>
            <PageTitleV2.Subtitle>
              <span className="text-sm font-semibold uppercase tracking-widest text-primary-400">
                Solicitation ID
              </span>
              <span className="rounded border border-primary-200 bg-primary-100 px-1.5 py-0.5 font-mono text-xs text-primary-700 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-300">
                {sbir.solicitation_number || sbir.number}
              </span>
            </PageTitleV2.Subtitle>
          </PageTitleV2.Content>
          <PageTitleV2.Actions className="lg:w-auto lg:flex-nowrap">
            <ToggleBookmarkIconButton
              bookmarkableType="sbir"
              objectId={sbir.id}
              iconSize={16}
              className="h-7 w-7 border border-primary-300 dark:border-primary-700 rounded-md bg-white dark:bg-transparent"
            />
            <Button variant="outline-compact" onClick={onShare}>
              <ShareIcon />
              Share
            </Button>
            {isUUID(activeOrgId) ? (
              <ResourcePermissionGate
                resourceType="organization"
                resourceId={activeOrgId}
                permission="write"
                fallback="hide"
              >
                <PursuitActions.Root
                  opportunity={opportunityInput}
                  searchId={searchId}
                >
                  <PursuitActions.BeginResponseButton className="w-full max-md:h-10 md:w-auto" />
                  <PursuitActions.AskAiButton className="w-full max-md:h-10 md:w-auto" />
                  <PursuitActions.CreatePursuitButton
                    variant="primary-compact"
                    label="Pursue"
                    loadingLabel="Pursuing..."
                    successLabel="Pursuing"
                    className="w-full max-md:h-10 md:w-auto"
                  />
                </PursuitActions.Root>
              </ResourcePermissionGate>
            ) : null}
          </PageTitleV2.Actions>
        </PageTitleV2.Row>
      </PageTitleV2>

      {/* Data Grid */}
      <div className="mb-4 grid grid-cols-2 gap-2 md:mb-6 md:grid-cols-4 md:gap-3">
        <DataBlockV2>
          <DataBlockV2.Header>
            <DataBlockV2.Icon icon={Building2} color="info" />
            <DataBlockV2.Label>Agency</DataBlockV2.Label>
          </DataBlockV2.Header>
          <DataBlockV2.Value title={sbir.agency}>
            {sbir.agency}
          </DataBlockV2.Value>
          {sbir.branch && (
            <DataBlockV2.SubValue>{sbir.branch}</DataBlockV2.SubValue>
          )}
        </DataBlockV2>
        <DataBlockV2>
          <DataBlockV2.Header>
            <DataBlockV2.Icon icon={Clock} color="warning" />
            <DataBlockV2.Label>Deadline</DataBlockV2.Label>
          </DataBlockV2.Header>
          <DataBlockV2.Value>{data.stats.closeDate ?? '—'}</DataBlockV2.Value>
          {(isClosed || daysRemaining) && (
            <DataBlockV2.SubValue>
              {isClosed ? 'Closed' : `${daysRemaining} days left`}
            </DataBlockV2.SubValue>
          )}
        </DataBlockV2>
        <DataBlockV2>
          <DataBlockV2.Header>
            <DataBlockV2.Icon icon={Calendar} color="accent" />
            <DataBlockV2.Label>Posted Date</DataBlockV2.Label>
          </DataBlockV2.Header>
          <DataBlockV2.Value>{data.stats.postedDate ?? '—'}</DataBlockV2.Value>
        </DataBlockV2>
        <DataBlockV2>
          <DataBlockV2.Header>
            <DataBlockV2.Icon icon={Layers} color="success" />
            <DataBlockV2.Label>Classification</DataBlockV2.Label>
          </DataBlockV2.Header>
          <DataBlockV2.Value>{sbir.program}</DataBlockV2.Value>
          <DataBlockV2.SubValue>Phase: {sbir.phase}</DataBlockV2.SubValue>
        </DataBlockV2>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Main Content Column */}
        <div
          className={twMerge(
            'flex flex-col gap-4',
            isDocumentsTab ? 'lg:col-span-12' : 'lg:col-span-8',
          )}
        >
          {/* Tabs */}
          <TabPanelV2
            value={activeTab}
            onValueChange={handleActiveTabChange}
            defaultTab="analysis"
          >
            <TabPanelV2.List>
              <TabPanelV2.Trigger value="analysis">
                <span className="flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" />
                  AI Analysis
                </span>
              </TabPanelV2.Trigger>
              <TabPanelV2.Trigger value="description">
                <span className="flex items-center gap-1.5">
                  <ListFilter className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Solicitation Details</span>
                  <span className="sm:hidden">Details</span>
                </span>
              </TabPanelV2.Trigger>
              <TabPanelV2.Trigger value="documents">
                <span className="flex items-center gap-1.5">
                  <Files className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">
                    SBIR Documents ({data.files.length})
                  </span>
                  <span className="sm:hidden">
                    Documents ({data.files.length})
                  </span>
                </span>
              </TabPanelV2.Trigger>
            </TabPanelV2.List>

            {/* Analysis Panel - card container */}
            <TabPanelV2.Content value="analysis">
              <CardV2 variant="accent" className="mt-2">
                <CardV2.Header>
                  <CardV2.HeaderIcon>
                    <Sparkles />
                  </CardV2.HeaderIcon>
                  <CardV2.HeaderTitle>
                    {sbir.program} Opportunity Analysis
                  </CardV2.HeaderTitle>
                </CardV2.Header>
                <CardV2.Content>
                  <ContentPanelV2 variant="inline">
                    <ContentPanelV2.Content markdown>
                      {sbir.ai_summary || 'No summary available.'}
                    </ContentPanelV2.Content>
                    <ContentPanelV2.Footer>
                      <ContentPanelV2.CopyButton />
                    </ContentPanelV2.Footer>
                  </ContentPanelV2>
                </CardV2.Content>
              </CardV2>
            </TabPanelV2.Content>

            {/* Description Panel - card container */}
            <TabPanelV2.Content value="description">
              <CardV2 variant="accent" className="mt-2">
                <CardV2.Header>
                  <CardV2.HeaderIcon>
                    <FileText />
                  </CardV2.HeaderIcon>
                  <CardV2.HeaderTitle>Solicitation Details</CardV2.HeaderTitle>
                </CardV2.Header>
                <CardV2.Content>
                  <ContentPanelV2 variant="inline">
                    <ReactMarkdown
                      rehypePlugins={[
                        rehypeRaw,
                        [rehypeSanitize, safeHtmlSchema],
                      ]}
                      components={markdownComponents}
                      className="prose prose-slate max-w-none prose-headings:font-semibold prose-headings:tracking-tight prose-headings:text-primary-900 prose-h2:mb-3 prose-h2:mt-6 prose-h2:border-b prose-h2:border-primary-200 prose-h2:pb-1.5 prose-h2:text-base first:prose-h2:mt-0 prose-p:text-[13px] prose-p:leading-6 prose-p:text-primary-800 prose-li:text-[13px] prose-li:text-primary-800 prose-li:marker:text-primary-500 prose-strong:font-semibold prose-strong:text-primary-900 dark:prose-invert dark:prose-headings:text-primary-100 dark:prose-h2:border-primary-700 dark:prose-p:text-primary-200 dark:prose-li:text-primary-200 dark:prose-strong:text-primary-100 [&_ol]:space-y-1 [&_ul]:space-y-1"
                    >
                      {sbir.description || 'No description available.'}
                    </ReactMarkdown>
                    <ContentPanelV2.Footer>
                      <ContentPanelV2.CopyButton />
                    </ContentPanelV2.Footer>
                  </ContentPanelV2>
                </CardV2.Content>
              </CardV2>
            </TabPanelV2.Content>

            <TabPanelV2.Content value="documents" className="mt-2">
              <OpportunityDocumentsTab
                files={data.files}
                persistenceKey={sbir.id}
                selectedFileId={selectedDocumentFileId}
                onSelectedFileIdChange={setSelectedDocumentFileId}
                selectionScrollRequestKey={documentFileScrollRequestKey}
                onSelectionScrollRequestHandled={() =>
                  setDocumentFileScrollRequestKey(null)
                }
                suppressDefaultListOpen={
                  documentsTabEntrySource === 'compact-file-click'
                }
                onAnalyzeFile={analyzeDocumentFile}
                isAnalyzingFile={isCreatingOpportunityChatSession}
                title="SBIR Documents"
                emptyTitle="No SBIR documents"
                emptyMessage="No documents available for this solicitation."
                searchLabel="Search SBIR documents"
              />
            </TabPanelV2.Content>
          </TabPanelV2>

          {!isDocumentsTab ? (
            <>
              {/* Divider below tabs */}
              <div className="mt-4 border-t border-primary-200 dark:border-primary-700" />

              {/* Documents Section */}
              <OpportunityDocumentsSection
                files={data.files}
                persistenceKey={sbir.id}
                isHighlighted={false}
                title="SBIR Documents"
                emptyTitle="No SBIR documents"
                emptyMessage="No documents available for this solicitation."
                searchLabel="Search SBIR documents"
                className="mt-3"
                onSelectFile={(file) =>
                  goToDocumentsTab(file.id, 'compact-file-click')
                }
              />

              {/* Related Opportunities Section - SEO optimized */}
              <SimilarOpportunitiesV2
                type="sbir"
                opportunities={similarOpportunities}
                isLoading={isSimilarLoading}
              >
                <SimilarOpportunitiesV2.Header>
                  Related SBIR/STTR Opportunities
                </SimilarOpportunitiesV2.Header>
                {isSimilarLoading ? (
                  <SimilarOpportunitiesV2.Skeleton />
                ) : (
                  <SimilarOpportunitiesV2.Grid>
                    {similarOpportunities.map((opp) => (
                      <SimilarOpportunitiesV2.Card
                        key={opp.id}
                        opportunity={opp}
                      />
                    ))}
                  </SimilarOpportunitiesV2.Grid>
                )}
              </SimilarOpportunitiesV2>
            </>
          ) : null}
        </div>

        {/* Sidebar */}
        {!isDocumentsTab ? (
          <div className="space-y-4 lg:col-span-4">
            {/* View Documents Button - hidden on mobile */}
            <ViewDocumentsButton
              fileCount={data.files.length}
              onClick={() => goToDocumentsTab(selectedDocumentFileId)}
              className="hidden lg:flex"
            />

            {/* Snapshot Widget */}
            <SidebarWidgetV2>
              <SidebarWidgetV2.Header icon={Activity}>
                Opportunity Snapshot
              </SidebarWidgetV2.Header>
              <SidebarWidgetV2.Content>
                {sbir.sbir_topic_link && (
                  <div>
                    <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                      Source System
                    </span>
                    <a
                      href={sbir.sbir_topic_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-1 text-sm font-medium text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300"
                    >
                      Official Link
                      <ExternalLink className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                    </a>
                  </div>
                )}
                <div>
                  <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                    Program Type
                  </span>
                  <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                    {sbir.program} - {sbir.phase}
                  </div>
                </div>
                <div>
                  <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                    Agency
                  </span>
                  <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                    {sbir.agency}
                    {sbir.branch && ` / ${sbir.branch}`}
                  </div>
                </div>
              </SidebarWidgetV2.Content>
            </SidebarWidgetV2>

            {/* Timeline Widget */}
            {data.timeline.length > 0 && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={Clock}>
                  {timelineTitle}
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  <TimelineV2>
                    {data.timeline.map((event) => (
                      <TimelineV2.Node
                        key={`${event.label}-${event.date}`}
                        date={event.date}
                        label={event.label}
                        status={event.status}
                      />
                    ))}
                  </TimelineV2>
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}
          </div>
        ) : null}
      </div>
    </>
  )
}

SbirDisplayV2.displayName = 'SbirDisplayV2'

export { SbirDisplayV2 }
export type { SbirDisplayV2Props }
